import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface TransformationImage {
  before: string;
  after: string;
  angle: string;
}

interface Transformation {
  name: string;
  duration: string;
  testimonial: string;
  images: TransformationImage[];
  achievements: string[];
}

const ImageCarousel: React.FC<{ images: TransformationImage[] }> = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageLoadError, setImageLoadError] = useState(false);

  const nextImage = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const previousImage = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.error('Image failed to load:', e.currentTarget.src);
    setImageLoadError(true);
  };

  return (
    <div className="relative overflow-hidden">
      <div className="flex space-x-4 w-full">
        <div className="flex-1 relative">
          <img 
            src={images[currentIndex].before} 
            alt={`Before - ${images[currentIndex].angle}`}
            className="w-full h-[400px] object-cover rounded transition-opacity duration-300"
            onError={handleImageError}
          />
          <span className="absolute top-4 left-4 bg-red-600 text-white px-4 py-1 rounded">BEFORE</span>
          {imageLoadError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900 rounded">
              <p className="text-white">Image failed to load</p>
            </div>
          )}
        </div>
        <div className="flex-1 relative">
          <img 
            src={images[currentIndex].after} 
            alt={`After - ${images[currentIndex].angle}`}
            className="w-full h-[400px] object-cover rounded transition-opacity duration-300"
            onError={handleImageError}
          />
          <span className="absolute top-4 right-4 bg-red-600 text-white px-4 py-1 rounded">AFTER</span>
          {imageLoadError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900 rounded">
              <p className="text-white">Image failed to load</p>
            </div>
          )}
        </div>
      </div>

      {/* Only show navigation if there's more than one image */}
      {images.length > 1 && (
        <>
          <button 
            onClick={previousImage}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-red-600 p-2 rounded-full text-white hover:bg-red-700 transition-colors z-10"
            aria-label="Previous image"
          >
            <ChevronLeft size={20} />
          </button>
          <button 
            onClick={nextImage}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-red-600 p-2 rounded-full text-white hover:bg-red-700 transition-colors z-10"
            aria-label="Next image"
          >
            <ChevronRight size={20} />
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentIndex ? 'bg-red-600' : 'bg-gray-400'
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const ClientResults: React.FC = () => {
  const transformations: Transformation[] = [
    {
      name: "Nick",
      duration: "10 Weeks",
      testimonial: "I struggled with my weight for years and nothing seemed to work long-term. With Kelvin's guidance, I finally found a sustainable approach that delivered incredible results. The transformation has been life-changing.",
      images: [
        {
          before: "/nfrontbefore.png.JPEG",
          after: "/nfrontafter.png.JPEG",
          angle: "Front View"
        },
        {
          before: "/nchestbefore.JPG",
          after: "/nchestafter.JPG",
          angle: "Chest View"
        },
        {
          before: "/nbackafter.png.JPEG",
          after: "/nsideafter.png.JPEG",
          angle: "Progress View"
        }
      ],
      achievements: [
        "Lost 12kg (from 85kg to 73kg) in 10 weeks",
        "Dramatically improved body composition",
        "Increased energy and confidence",
        "Developed sustainable healthy habits"
      ]
    },
    {
      name: "Kelvin",
      duration: "10 Weeks",
      testimonial: "I was in your shoes once, struggling to lose weight and feeling hopeless and whatever I tried didnt seem to work. But once I had a clear plan in place and a clear goal, I was able to achieve my goal and loose 10kg in 10 weeks.",
      images: [
        {
          before: "/kbefore.png",
          after: "/kafter.png",
          angle: "Front View"
        },
        {
          before: "/kbackbefore.png",
          after: "/kbackafter.png",
          angle: "Back View"
        },
        {
          before: "/ksidebefore.png",
          after: "/ksideafter.png",
          angle: "Side View"
        }
      ],
      achievements: [
        "Lost 10kg of body fat",
        "Increased strength significantly",
        "Improved overall fitness level",
        "Better energy throughout the day"
      ]
    },
    {
      name: "Elliot",
      duration: "4 Weeks",
      testimonial: "After trying countless fitness and meals plans I felt I was out of time and had very minimal progress. Once I found Kelvin I knew I was saved. He gave a crystal clear plan and I was able to transform my body and loose 4kg in 4 weeks.",
      images: [
        {
          before: "/brofbefore.JPG",
          after: "/brofafter.JPG",
          angle: "Front View"
        },
        {
          before: "/brobefore.JPG",
          after: "/broafter.JPG",
          angle: "Side View"
        }
      ],
      achievements: [
        "Lost 4kg in only 4 weeks",
        "Built lean muscle mass",
        "Enhanced athletic performance",
        "Improved nutrition habits"
      ]
    },
    {
      name: "Declan",
      duration: "2 Months",
      testimonial: "I was struggling to gain muscle mass and was not seeing any progress. Once I joined the program I was able to gain 3kg in only 2 weeks and I am still going strong.",
      images: [
          {
            before: "/bbefore.png",
            after: "/bafter.png",
            angle: "Front View"
          },
          {
            before: "/bbackbefore.png",
            after: "/bbackafter.png",
            angle: "Back View"
          },
          {
            before: "/bsidebefore.jpg",
            after: "/bsideafter.jpg",
            angle: "Side View"
          }
      ],
      achievements: [
        "gained 5kg in 2 months",
        "Completely transformed body composition",
        "Developed sustainable fitness habits",
        "Increased confidence and energy"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-black overflow-x-hidden">
      {/* Hero Section */}
      <div className="relative pt-20 py-20  mt-10 bg-gradient-to-b from-black to-gray-900 overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-semibold text-white mb-6 text-glow">
            REAL RESULTS, REAL TRANSFORMATIONS
          </h1>
          <div className="w-16 h-1 bg-red-600 mx-auto mb-8"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            See how some of our clients have transformed their bodies and lives through dedication, 
            hard work, and our proven training methods.
          </p>
        </div>
      </div>

      {/* Transformations Grid */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-16">
        {transformations.map((client, index) => (
          <div key={index} className="mb-20 bg-gray-900/30 rounded-lg overflow-hidden">
            <div className="grid md:grid-cols-2 gap-4 sm:gap-8 p-2 sm:p-8">
              {/* Before/After Images Carousel */}
              <div className="space-y-4">
                <ImageCarousel images={client.images} />
              </div>

              {/* Client Details */}
              <div className="flex flex-col justify-center">
                <h2 className="text-3xl font-semibold text-white mb-4">{client.name}'S TRANSFORMATION</h2>
                <p className="text-red-500 text-lg mb-6">Duration: {client.duration}</p>
                <p className="text-gray-300 mb-8 italic">"{client.testimonial}"</p>
                
                <h3 className="text-white text-xl mb-4">Achievements:</h3>
                <ul className="space-y-2">
                  {client.achievements.map((achievement, idx) => (
                    <li key={idx} className="flex items-center text-gray-300">
                      <span className="w-2 h-2 bg-red-600 rounded-full mr-3"></span>
                      {achievement}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-b from-gray-900 to-black py-20">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
            READY TO START YOUR TRANSFORMATION?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Join our program and become our next success story. Get personalized training 
            and nutrition plans designed specifically for your goals.
          </p>
          <Link 
            to="/contact" 
            className="inline-block bg-red-600 text-white px-8 py-3 rounded hover:bg-red-700 transition-colors"
          >
            Start Your Journey Now
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClientResults; 